import { useState, useRef, useEffect } from 'react'
import { FileText, Check, CheckCircle2, Plus, Send, PanelRightClose, User, Bot } from 'lucide-react'

interface AssistantPanelProps {
  onClose?: () => void
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
}

export default function AssistantPanel({ onClose }: AssistantPanelProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSendMessage = () => {
    if (!inputValue.trim()) return

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
    }

    setMessages((prev) => [...prev, userMsg])
    setInputValue('')

    // Mock assistant responses one by one
    const mockResponses = [
      '收到，正在分析您的请求...',
      '我已根据您的指示调整了视频脚本。',
      '正在重新生成预览片段，请稍候...',
      '预览已更新！您可以检查一下效果。',
      '还有其他地方需要修改吗？',
    ]

    let delay = 1000
    mockResponses.forEach((response, index) => {
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + index).toString(),
            role: 'assistant',
            content: response,
          },
        ])
      }, delay)
      delay += 1500 + Math.random() * 1000
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage()
    }
  }

  return (
    <div className="flex h-full w-80 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <h2 className="font-semibold text-gray-900">Chat</h2>
        <button
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <PanelRightClose className="h-4 w-4" />
        </button>
      </div>
      <div className="custom-scrollbar flex-1 overflow-y-auto p-4">
        <div className="mb-6">
          <p className="mb-3 text-xs font-medium uppercase tracking-wider text-gray-500">
            Task Status
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-600 transition-colors hover:border-gray-200 hover:bg-gray-50">
              <div className="flex items-center gap-2.5">
                <div className="flex h-6 w-6 items-center justify-center rounded bg-white shadow-sm ring-1 ring-black/5">
                  <FileText className="h-3.5 w-3.5 text-gray-400" />
                </div>
                <span className="font-medium">Read Script</span>
              </div>
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-600">
                <Check className="h-3 w-3" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-600 transition-colors hover:border-gray-200 hover:bg-gray-50">
              <div className="flex items-center gap-2.5">
                <div className="flex h-6 w-6 items-center justify-center rounded bg-white shadow-sm ring-1 ring-black/5">
                  <FileText className="h-3.5 w-3.5 text-gray-400" />
                </div>
                <span className="font-medium">Edited Script</span>
              </div>
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-600">
                <Check className="h-3 w-3" />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
            <div className="mb-3 flex items-center gap-2 text-blue-700">
              <CheckCircle2 className="h-5 w-5" />
              <h3 className="font-bold">视频制作完成！</h3>
            </div>
            <p className="mb-4 text-sm leading-relaxed text-gray-700">
              我已经成功创建了一个 92.5 秒的搞笑视频，内容是 G8
              国家元首用&quot;七大姑八大姨&quot;的口吻送新年祝福！
            </p>

            <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-900">
              📺 视频内容概览
            </h4>
            <p className="mb-2 text-xs text-gray-600">7 位虚构元首角色，依次登场：</p>
            <ul className="mb-4 list-decimal space-y-1.5 pl-4 text-xs text-gray-600">
              <li>
                <span className="font-medium text-gray-900">美国总统</span> (0-14 秒) -
                关心跳槽和对象问题
              </li>
              <li>
                <span className="font-medium text-gray-900">英国首相</span> (14-27 秒) -
                建议考公务员求稳定
              </li>
              <li>
                <span className="font-medium text-gray-900">法国总统</span> (27-40 秒) -
                催婚介绍对象
              </li>
              <li>
                <span className="font-medium text-gray-900">德国总理</span> (40-53 秒) -
                询问买房存款
              </li>
              <li>
                <span className="font-medium text-gray-900">日本首相</span> (53-66 秒) -
                关心年终奖和回家
              </li>
            </ul>

            <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-900">
              🎬 技术规格
            </h4>
            <ul className="list-disc space-y-1 pl-4 text-xs text-gray-600">
              <li>总时长：92.5 秒</li>
              <li>视频片段：20 个动态镜头</li>
              <li>配音：7 段中文&quot;琴婶&quot;音色</li>
              <li>字幕：37 个自动生成的中文本幕</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`flex max-w-[85%] items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${msg.role === 'user' ? 'bg-gray-900 text-white' : 'bg-blue-100 text-blue-600'}`}
                >
                  {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                </div>
                <div
                  className={`rounded-xl p-3 text-sm shadow-sm ${msg.role === 'user' ? 'bg-gray-900 text-white' : 'border border-gray-100 bg-white text-gray-700'}`}
                >
                  {msg.content}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/50 p-4">
        <div className="relative">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything..."
            className="no-focus-ring w-full rounded-xl border-gray-200 bg-white py-3 pl-4 pr-12 text-sm shadow-sm outline-none transition-all placeholder:text-gray-400 focus:border-gray-200 focus:ring-0"
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 gap-1">
            <button className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
              <Plus className="h-4 w-4" />
            </button>
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
              className={`rounded-lg p-1.5 shadow-sm transition-colors ${!inputValue.trim() ? 'cursor-not-allowed bg-gray-200 text-gray-400' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
            >
              <Send className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
